import React, { useEffect, useRef } from 'react';

/**
 * "Welcome Back" modal shown when a duplicate patient is detected on Step 1.
 * Single follow-up button only — existing patients cannot continue as new.
 */
const DuplicatePatientModal = ({ isOpen, patientName, onFollowup, onClose }) => {
  const modalRef = useRef(null);
  const firstBtnRef = useRef(null);

  // Focus trap + ESC close
  useEffect(() => {
    if (!isOpen) return;
    firstBtnRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab') {
        const focusable = modalRef.current?.querySelectorAll('button');
        if (!focusable?.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = ''; };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000, padding: '20px',
    }} onClick={onClose}>
      <div ref={modalRef} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="dup-title" style={{
        background: '#fff', borderRadius: '16px', maxWidth: '440px', width: '100%', padding: '32px 28px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative', animation: 'modalSlideIn 0.3s ease',
      }}>
        {/* Close button */}
        <button onClick={onClose} style={{
          position: 'absolute', top: '12px', right: '12px', background: 'none', border: 'none',
          fontSize: '22px', color: '#999', cursor: 'pointer', padding: '4px 8px', lineHeight: 1,
        }} aria-label="Close">×</button>

        {/* Icon */}
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '50%', background: '#e8f5e9',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4caf50" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 id="dup-title" style={{
          textAlign: 'center', fontSize: '22px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 8px',
        }}>Welcome Back!</h2>

        {/* Message */}
        <p style={{ textAlign: 'center', color: '#666', fontSize: '14px', lineHeight: '1.6', margin: '0 0 24px' }}>
          {patientName ? (
            <>Hi <strong>{patientName}</strong>, we already have your information on file. What would you like to do?</>
          ) : (
            <>We already have your information on file. What would you like to do?</>
          )}
        </p>

        {/* Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button ref={firstBtnRef} onClick={onFollowup} style={{
            width: '100%', padding: '13px 16px', fontSize: '14px', fontWeight: '600', border: 'none',
            borderRadius: '10px', cursor: 'pointer', color: '#fff',
            background: 'var(--color-primary, #1a7fc1)', transition: 'opacity 0.2s',
          }}>
            Go to Follow-up Form
          </button>

        </div>
      </div>

      <style>{`
        @keyframes modalSlideIn {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default DuplicatePatientModal;
